import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, Smartphone, CheckCircle, Loader2, Copy, ExternalLink, QrCode, Key } from 'lucide-react';
import { toast } from 'sonner';
import TabTip from '../ui/TabTip';

const isMobileDevice = () => /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

function SetupMethodButtons({ onSetup2FA, actionLoading }) {
  return (
    <div className="grid gap-3">
      <Button onClick={() => onSetup2FA('totp')} disabled={actionLoading} className="justify-start" variant="outline" data-testid="setup-totp-btn">
        <Smartphone className="w-4 h-4 mr-2" />Set Up Authenticator App
      </Button>
    </div>
  );
}

function CopyableSecret({ secret }) {
  const [copied, setCopied] = useState(false);
  const formatted = secret ? secret.replace(/(.{4})/g, '$1 ').trim() : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(secret).then(() => {
      setCopied(true);
      toast.success('Secret key copied');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error('Failed to copy'));
  };

  return (
    <div className="bg-slate-100 rounded-lg p-3">
      <p className="text-xs text-slate-500 mb-1.5 font-medium">Secret Key (manual entry)</p>
      <div className="flex items-center gap-2">
        <code className="text-sm font-mono text-slate-800 tracking-wider flex-1 break-all">{formatted}</code>
        <Button variant="ghost" size="sm" onClick={handleCopy} className="shrink-0" data-testid="copy-secret-btn">
          {copied ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

function MobileSetup({ otpauthUri, totpSecret }) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-slate-700">Add to your authenticator app:</p>
      <div className="space-y-2">
        <p className="text-xs text-slate-500 font-medium">Option 1: Open directly</p>
        <a
          href={otpauthUri}
          data-testid="deeplink-btn"
          className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm transition-colors"
        >
          <ExternalLink className="w-4 h-4" />Add to Authenticator App
        </a>
        <p className="text-xs text-slate-400 text-center">Opens Google Authenticator, Authy, or Microsoft Authenticator</p>
      </div>
      <div className="space-y-2 pt-2 border-t border-slate-200">
        <p className="text-xs text-slate-500 font-medium">Option 2: Enter key manually in your authenticator app</p>
        <CopyableSecret secret={totpSecret} />
      </div>
    </div>
  );
}

function DesktopSetup({ qrCodeUrl, totpSecret }) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-slate-700">Add to your authenticator app:</p>
      <div className="space-y-2">
        <p className="text-xs text-slate-500 font-medium">Option 1: Scan QR code with your phone</p>
        <div className="flex justify-center">
          <img src={qrCodeUrl} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
        </div>
      </div>
      <div className="space-y-2 pt-2 border-t border-slate-200">
        <p className="text-xs text-slate-500 font-medium">Option 2: Enter key manually in your authenticator app</p>
        <CopyableSecret secret={totpSecret} />
      </div>
    </div>
  );
}

function VerificationInput({ verificationCode, setVerificationCode, onVerify, onCancel, actionLoading, setError }) {
  return (
    <div className="space-y-3 pt-3 border-t border-slate-200">
      <div className="space-y-2">
        <label className="text-sm font-medium">Enter the 6-digit code from your authenticator app:</label>
        <input
          type="text"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-center text-lg font-mono tracking-widest"
          maxLength={6}
          data-testid="2fa-verification-input"
        />
      </div>
      <div className="flex space-x-2">
        <Button onClick={onVerify} disabled={actionLoading || verificationCode.length !== 6} className="flex-1" data-testid="verify-2fa-btn">
          {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Enable'}
        </Button>
        <Button onClick={() => { onCancel(false); setVerificationCode(''); setError(''); }} variant="outline">Cancel</Button>
      </div>
    </div>
  );
}

const TwoFactorCard = ({
  twoFactorStatus, actionLoading, onSetup2FA, onVerify2FA, onDisable2FA,
  showQRCode, qrCodeUrl, totpSecret, otpauthUri,
  verificationCode, setVerificationCode, setShowQRCode, setError
}) => (
  <Card>
    <CardHeader>
      <div className="flex items-center justify-between">
        <div>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5" /><span>Two-Factor Authentication</span><TabTip tip="two_factor_auth" showIcon={true} />
          </CardTitle>
          <CardDescription>Add an extra layer of security to your account</CardDescription>
        </div>
        {twoFactorStatus?.enabled && (
          <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3 mr-1" />Enabled</Badge>
        )}
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      {!twoFactorStatus?.enabled ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Protect your account by requiring a second verification step when signing in.</p>
          <SetupMethodButtons onSetup2FA={onSetup2FA} actionLoading={actionLoading} />
          {showQRCode && (qrCodeUrl || otpauthUri) && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg space-y-4" data-testid="2fa-setup-panel">
              {isMobileDevice()
                ? <MobileSetup otpauthUri={otpauthUri} totpSecret={totpSecret} />
                : <DesktopSetup qrCodeUrl={qrCodeUrl} totpSecret={totpSecret} />
              }
              <VerificationInput
                verificationCode={verificationCode}
                setVerificationCode={setVerificationCode}
                onVerify={onVerify2FA}
                onCancel={setShowQRCode}
                actionLoading={actionLoading}
                setError={setError}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center space-x-2 text-sm">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-slate-700">Two-factor authentication is enabled via <span className="font-medium capitalize">{twoFactorStatus.method || 'Authenticator App'}</span></span>
          </div>
          <Button onClick={onDisable2FA} disabled={actionLoading} variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50">Disable 2FA</Button>
        </div>
      )}
    </CardContent>
  </Card>
);

export default TwoFactorCard;
